import { createRoute, type AnyRoute } from "@tanstack/react-router";
import { TrendingUp } from "lucide-react";

import type { FeatureModule } from "@/features/types";
import { guarded } from "@/lib/auth";

import type { SortKey } from "./lib";
import { ExpansionPage } from "./pages/ExpansionPage";

/** Search params on /expansion. Filters + sort live in the URL so a filtered
 *  view is shareable; `account` (+ `expanded`) drive the account drawer, same
 *  convention as Upsell Ops / Customer Tiers. */
export interface ExpansionSearch {
  q?: string;
  /** Comma list of tiers, e.g. "1,2". */
  tiers?: string;
  csm?: string;
  /** Only accounts over their contracted allotment. */
  over?: boolean;
  /** Only accounts with data-quality flags. */
  attention?: boolean;
  sort?: SortKey;
  account?: number;
  expanded?: boolean;
}

const SORTS: SortKey[] = ["bill", "over", "name", "tier", "usage"];

function validateExpansionSearch(raw: Record<string, unknown>): ExpansionSearch {
  const out: ExpansionSearch = {};
  if (typeof raw.q === "string" && raw.q) out.q = raw.q;
  if (typeof raw.tiers === "string" && raw.tiers) out.tiers = raw.tiers;
  if (typeof raw.csm === "string" && raw.csm) out.csm = raw.csm;
  if (raw.over === true || raw.over === "1" || raw.over === "true") out.over = true;
  if (raw.attention === true || raw.attention === "1" || raw.attention === "true") {
    out.attention = true;
  }
  if (SORTS.includes(raw.sort as SortKey)) out.sort = raw.sort as SortKey;
  if (raw.account !== undefined && raw.account !== "") {
    const n = Number(raw.account);
    if (Number.isFinite(n)) out.account = n;
  }
  if (raw.expanded === true || raw.expanded === "1" || raw.expanded === "true") {
    out.expanded = true;
  }
  return out;
}

export const expansionModule: FeatureModule = {
  key: "expansion",
  nav: {
    label: "Expansion",
    icon: TrendingUp,
    to: "/expansion",
    requires: "expansion.read",
    order: 41,
    group: "Revenue",
  },
  routes: (rootRoute) => [
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/expansion",
      component: guarded("expansion.read", ExpansionPage),
      validateSearch: validateExpansionSearch,
    }),
  ] as unknown as AnyRoute[],
};
